import { Icon } from "@resource/atlas";
import { atlasRing, atlasRingWarning } from "@resource/atlas/icons";
import clsx from "clsx";
import { useMemo } from "react";
import { Droppable } from "react-beautiful-dnd";

import ATSStage from "./ATSStage";
import { GuideTemplateStage, Stage } from "./GuideTemplateMapper";

interface Props {
  index: number;
  stages: Stage[];
  guideTemplateStage: GuideTemplateStage;
  draggingStage: Stage | null;
  forMultipleJobs: boolean;
  isOverThis: boolean;
}

function GuideTemplateStage({
  guideTemplateStage,
  stages,
  index,
  isOverThis,
  draggingStage,
  forMultipleJobs,
}: Props) {
  const canDrop = useMemo(() => {
    const isSource = stages.some((stage) => stage.id === draggingStage?.id);
    if (!isSource) {
      const jobRoleGuideTemplateIds = stages.reduce(
        (ids, stage) => ids.concat(stage.jobRoleGuideTemplateIds),
        [] as string[]
      );
      const hasCommonJob = draggingStage?.jobRoleGuideTemplateIds.some(
        (value) => jobRoleGuideTemplateIds.includes(value)
      );
      return !hasCommonJob;
    }
    return true;
  }, [stages, draggingStage]);

  const showError = !canDrop && isOverThis;

  return (
    <div
      className={clsx("rounded-md mt-4 p-5 bg-white", {
        "shadow-sm": !showError,
        "border-2 border-orange-500": showError,
      })}
    >
      <div className="relative">
        <h3 className="flex text-body-md gap-1">
          <div className="w-5 h-5 relative text-subtle">
            <Icon className="absolute inset-0" content={atlasRing} />
            <span className="absolute inset-0 text-body-sm text-[.625rem] text-center w-5 leading-5">
              {index + 1}
            </span>
          </div>
          <div className="truncate">{guideTemplateStage.name}</div>
        </h3>
        {showError && (
          <div className="flex items-center text-body-md text-orange-500 bg-white absolute inset-0">
            <Icon className="inline-block mr-2" content={atlasRingWarning} />
            <span>You cannot map more than one stage per job.</span>
          </div>
        )}
      </div>
      <Droppable droppableId={guideTemplateStage.id} isDropDisabled={showError}>
        {(provided, snapshot) => (
          <div
            className="relative mt-4 min-h-[3.25rem]"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {stages.map((stage, stageIndex) => (
              <div key={stage.id} className="mt-3">
                <ATSStage
                  stage={stage}
                  index={stageIndex}
                  showJobsCount={forMultipleJobs}
                />
              </div>
            ))}
            {canDrop && !snapshot.isDraggingOver && stages.length === 0 && (
              <div className="flex justify-center items-center text-body-md text-subtle rounded-md p-4 bg-light-gray-200 absolute inset-x-0 top-0">
                <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                  Drag and drop unmapped stages here
                </span>
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export default GuideTemplateStage;
